import type { AuthResponse, AuthCredentials } from "@ghostland/shared";
import { API_BASE_URL } from "../config.js";

export class AuthError extends Error {}

async function post<T>(path: string, body?: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    // Always include cookies so the httpOnly refresh token is sent/received.
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new AuthError(message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function register(creds: AuthCredentials): Promise<AuthResponse> {
  return post<AuthResponse>("/auth/register", creds);
}

export function login(creds: AuthCredentials): Promise<AuthResponse> {
  return post<AuthResponse>("/auth/login", creds);
}

/** Exchange the httpOnly refresh cookie for a fresh access token. */
export function refresh(): Promise<AuthResponse> {
  return post<AuthResponse>("/auth/refresh");
}

export function logout(): Promise<void> {
  return post<void>("/auth/logout");
}

/** Confirm an account's email via the token from the confirmation link. */
export function verifyEmail(token: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>("/auth/verify", { token });
}

/** Resend the confirmation email to the (logged-in, unverified) account. */
export function resendVerification(accessToken: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>("/auth/resend", undefined, accessToken);
}
